import { MessageSquareText, Search, FileCheck2, Handshake } from "lucide-react";
import { Reveal } from "./Motion";
import { StatusBadge } from "./AssetCard";

const PRINCIPLES = [
  { icon: MessageSquareText, title: "Honest, transparent communication", text: "Straightforward answers, and we keep you informed throughout the sourcing and purchasing process." },
  { icon: Search, title: "We start with your requirements", text: "We work to understand each client's asset requirements before we search, so effort goes toward suitable options." },
  { icon: Handshake, title: "Relevant channels & relationships", text: "We search relevant channels, sellers and business relationships across our four divisions." },
  { icon: FileCheck2, title: "Accurate information, informed decisions", text: "We share the asset information available to us so you can make an informed purchasing decision, with professional service throughout." },
];

const MATRIX = [
  ["available", "The asset is currently available. Check the listing's ownership note to see who is selling it."],
  ["being_sourced", "Selby Asset Group is sourcing this type of asset on behalf of a client. It may not be available to others."],
  ["pending", "A sale is in progress. You may still inquire in case the transaction does not complete."],
  ["reserved", "The asset is held for a buyer and is not currently available."],
  ["unavailable", "The asset is temporarily not available for purchase."],
  ["sold", "The asset has been sold and is removed from active inventory."],
];

const OWNERSHIP = [
  ["Owned by Selby Asset Group", "Shown only when the company owns the asset."],
  ["Listed through a third-party seller", "The seller owns the asset; we help facilitate your inquiry."],
  ["Sourced for a client", "Identified through our channels and relationships for a specific client request."],
];

export const TrustSection = () => (
  <section className="relative py-24 md:py-36 bg-[var(--paper)] text-[#0a0d14]" data-testid="trust-section">
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-5 lg:sticky lg:top-32 self-start">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#8a6d12]">Trust & Transparency</p>
          <h2 className="font-display text-5xl md:text-6xl leading-[1] mt-6">How we do <em>business.</em></h2>
          <p className="mt-6 text-[#2b3140] leading-relaxed">
            Not every asset we present is owned by Selby Asset Group. Each listing states its availability and whether it is owned by the company, listed through a third-party seller, or sourced for a client.
          </p>
          <p className="mt-4 text-sm text-[#4a5163] leading-relaxed border-l-2 border-[#b8941f] pl-4">
            Unless a listing explicitly says otherwise, assets have not been independently inspected or verified by Selby Asset Group. We encourage every buyer to inspect and confirm details before purchase.
          </p>
        </Reveal>
        <div className="lg:col-span-7 space-y-14">
          <div className="grid sm:grid-cols-2 gap-px bg-[#0a0d14]/15 border border-[#0a0d14]/15">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06} className="bg-[var(--paper)] p-7 md:p-8 group">
                <p.icon className="w-6 h-6 text-[#8a6d12] transition-transform duration-500 group-hover:-translate-y-1" />
                <h3 className="font-display text-2xl mt-5 leading-tight">{p.title}</h3>
                <p className="text-sm text-[#3a4152] mt-3 leading-relaxed">{p.text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#8a6d12] mb-5">Listing status guide</p>
            <div className="bg-[#0a0d14] divide-y divide-white/10" data-testid="status-matrix">
              {MATRIX.map(([k, text]) => (
                <div key={k} className="grid sm:grid-cols-[170px_1fr] gap-3 sm:gap-6 p-5 items-start">
                  <StatusBadge status={k} className="justify-self-start" />
                  <p className="text-sm text-slate-300">{text}</p>
                </div>
              ))}
            </div>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#8a6d12] mt-10 mb-5">Ownership on every listing</p>
            <div className="grid sm:grid-cols-3 gap-px bg-[#0a0d14]/15 border border-[#0a0d14]/15" data-testid="ownership-guide">
              {OWNERSHIP.map(([t, d]) => (
                <div key={t} className="bg-[var(--paper)] p-5">
                  <p className="font-display text-xl leading-tight">{t}</p>
                  <p className="text-sm text-[#3a4152] mt-2">{d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
